import { useMemo, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./flow-year-report-style.css";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import SiteFooter from "../../../custom/site-footer/site-footer";
import useIsMobile from "../../../../hooks/useIsMobile";
import { shareOrCopy } from "../../../../utils/share";

//分享 / 官方 LINE（footer 用）
const SHARE_URL = "https://yuanchen.leinotek.com/";
const LINE_URL = "https://lin.ee/v05Pe75";
const handleShare = (e) => {
  e.preventDefault();
  shareOrCopy({
    title: "元辰雲閣",
    text: "元辰雲閣｜流年之書・看懂今年運勢節奏",
    url: SHARE_URL,
  }).then((r) => {
    if (r === "copied") window.alert("已複製連結，貼上即可分享");
  });
};

//流年之書專屬 isMobile：
//  手機（短邊 < 600）→ mobile JSX
//  iPad mini / Air 直式（< 1024 寬 portrait）→ mobile JSX
//  iPad Pro 直式 + 全部橫式 → web JSX
function calcBookIsMobile() {
  if (typeof window === "undefined") return false;
  const w = window.innerWidth;
  const h = window.innerHeight;
  const shortSide = Math.min(w, h);
  if (shortSide < 600) return true; // 手機
  const isPortrait = h > w;
  if (isPortrait && w < 1024) return true; // iPad mini/Air 直
  return false; // iPad Pro 直 + 所有橫式
}

function useIsBookPhone() {
  const [isMobile, setIsMobile] = useState(calcBookIsMobile);
  useEffect(() => {
    const onChange = () => setIsMobile(calcBookIsMobile());
    window.addEventListener("resize", onChange);
    window.addEventListener("orientationchange", onChange);
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("orientationchange", onChange);
    };
  }, []);
  return isMobile;
}

//假資料（直接造訪 /flow-report 時的預覽 fallback；正式流程由 location.state.data 帶入）
const mockData = {
  orderID: "DI_MOCK_FLOW",
  name: "林聖凱",
  ganShiYear: "丙午",
  year: 2026,
  productID: "1234567890",
  summary: {
    preface: {
      title: "權印相生，淬火成器之年",
      content:
        "<p>在你翻開這本書的此刻，請先將所有對「運勢」的既定印象放下。<br />這不是一本預測你宿命的劇本，因為你才是自己人生的主角。這也不是一本告訴你吉凶的答案之書，因為挑戰與機遇往往是同一枚硬幣的兩面。<br /><br /><b>請將它視為一本「年度個人戰略手冊」。</b><br /><br />根據你的命盤（己酉日主，身弱），2025年，宇宙為你設定了一個極具挑戰性的課題：一股前所未有的<b class='highlight-dark'>壓力、挑戰與權力（七殺）</b>，將從天而降，直接衝擊你。然而，與此同時，一股強大的貴人、支援與庇護（正印）之力，也將從地而生，全力守護你。<br /><br />今年，你將親身體驗「殺印相生」這個命理中至為深刻的轉化過程。這本書的目的，就是與你並肩作戰，清晰地標示出壓力的來源、找到支援的位置，並陪你一起，將今年的所有試煉，親手「淬鍊」成你晉升更高階位的權柄與勳章。<br /><br />現在，讓我們一起，開始這場淬火成器的旅程。</p>",
    },
    review: {
      title: "奠基與礪煉之年",
      content:
        "<p>在展望未來之前，我們必須先回首來時路。2025<b>【甲辰】</b>年，對你而言，主題是「規則下的競爭」。<br /><br />流年甲木正官，帶來了規則、責任與上級的約束。你必須在一個既定的框架內行事。然而，在地支，流年辰土是你的劫財，代表著同輩的競爭、合作與潛在的利益衝突。<br /><br />因此，你2025年的真實寫照是：<b>在遵守規則、履行職責的同時，必須應對來自周遭的激烈競爭與人際關係的暗流。</b>這一年，你學會了如何在體制內周旋與自保，雖然辛苦，卻也為2025年應對更巨大的壓力，打下了如何在複雜局勢中生存的基礎。</p>",
    },
    inscription: {
      word: "淬",
      simple:
        "<p>「淬」，是烈火與流水的交替，是壓力與支援的共舞。這場淬鍊反應，有兩條核心方程式：</p>",
      challenge:
        "<p><b>1. 核心挑戰：高壓突襲與極限施壓</b><br />今年你會面臨許多突如其來、甚至不講理的施壓與刁難。宇宙為你安排的考驗，往往伴隨著強烈的挫折感與不適，這是一系列高難度的「升級任務」。難纏的小人針對、外界的質疑，以及那些逼人的爛事，將不斷衝擊你的底線。在這個局勢下，你最大的危機就是被負面情緒淹沒，或者選擇當個默默吃苦的隱形人，獨自把委屈吞下去、死撐硬扛。</p>",
      method:
        "<p><b>2. 核心解方：借力打力與實績反擊</b><br />破局的最高戰略，就是絕不單打獨鬥！遇見難題請立刻向主管或長輩求援，借他們的智慧與權威擋下攻擊，成為你最堅固的護盾。在保護傘下冷靜吸收經驗，將重擔轉化為未來晉升的籌碼。最後大膽展現天賦，別害怕高調！用無可挑剔的亮眼實績主動出擊，直接粉碎所有質疑，霸氣奪回人生主導權！</p>",
    },
    field: {
      career: {
        score: 4,
        title: "風暴中的舵手",
        desc: "事業是你今年的主戰場。空前的壓力與挑戰，會逼你爆發出所有潛能。你將有機會解決重大難題、或被賦予重任，從而獲得實質的權力與地位。",
      },
      money: {
        score: 3,
        title: "以技換糧",
        desc: "才華被催旺，專業技能與創意作品將成為主要的生財之道。但開銷也大，財來財去難有積蓄，是辛苦耕耘、以技藝換取回報的一年。",
      },
      love: {
        score: 2,
        title: "獨行的旅人",
        desc: "全年心神都被事業與健康所佔據，感情之事難免有心無力。也可能帶來短暫但充滿壓力的關係。有伴者需注意外部壓力影響內部和諧。",
      },
      social: {
        score: 3,
        title: "貴人小人並存",
        desc: "人際關係極度兩極化。一方面會出現公開的敵人或暗中的小人；另一方面也會有長輩貴人力挺。你將深刻體會誰是敵、誰是友。",
      },
      family: {
        score: 4,
        title: "溫暖的港灣",
        desc: "家庭、母親、長輩是你今年最強大的後盾。無論在外遭遇多大的風雨，家就是你獲得能量補充與心靈慰藉的最終港灣。",
      },
      health: {
        score: 1,
        title: "瀕臨極限",
        desc: "年度最高警報。今年是對健康最嚴峻的考驗，身體如同滿弓之弦時刻處於緊繃狀態，所有成就都可能以透支健康為代價。",
      },
    },
    //大運屬「總論」範疇，收在 summary 內（與 preface/review/inscription 同層）
    daYun: {
      ganZhi: "戊申",
      half: "下半",
      tenGod: "傷官",
      situation:
        "這個階段的能量主軸是「向外展現、主動出擊」——你會比過去更想被看見、更想把想法與才華端上檯面，不甘於只待在原地。整體底色帶有一股鋒芒外露的氣勢，讓你在表達與創造上特別活躍。",
      direction:
        "這是一段適合累積作品、磨亮專業、建立個人辨識度的時期。大方向是把鋒芒打磨好、把舞台拓出去，每一年會有各自的節奏；同時也要記得收放有度、慎防直率變成樹敵，給自己留一點轉圜的空間。",
    },
  },
  career: {
    degree: 3,
    imagery: "風暴中的舵手，駕馭狂瀾",
    content:
      "<p>今年的職場注定是一場沒有「舒適圈」的硬仗。你會感覺自己宛如被捲入一場突如其來的風暴，毫無防備地被推向事業的風口浪尖。在這個階段，你極有可能會面臨空降且難搞的主管、接手大家都不想碰的燙手專案、被迫捲入公司內部的權力鬥爭，甚至必須一肩扛下遠遠超出你目前負荷的重大責任。這些排山倒海而來的挑戰，都在對你的抗壓性進行極限測試。<br /><br />然而，面對這樣險惡的局勢，你絕對不會在這場大浪中翻船。因為在你的背後，其實隱藏著非常強大且穩固的防護罩。這份底氣來自於你過去日積月累打下的紮實口碑，以及更高層次主管對你默默的信任與認可。即使外在環境再混亂，公司既有的制度、你手邊掌握的資源，以及長輩貴人的暗中支持，都會化作最安定的船舵，在關鍵時刻把你牢牢托住，讓你擁有放手一搏的安全感。<br /><br />想要安然穿越這場風暴，並在最後漂亮收尾，你的戰略必須非常清晰：千萬不要去瞎攪和那些複雜的職場政治與人事糾紛。在這段期間，你手裡唯一且最強的武器，就是你無可取代的「專業硬實力」。把所有的專注力都放在解決問題上，將你的才華與執行力發揮到極致。面對那些逼人的爛事與難題，直接用無可挑剔的亮眼實績去輾壓破局，這就是你最好的反擊！</p>",
    monthHint:
      "<p><b>3月：頂住高壓測試</b><br />這是全年的第一道大關卡，突發狀況與沉重工作量將帶來最劇烈的壓力。請咬牙穩住步調，先確保基礎任務不失分。<br /><br /><b>8月：避開權力碰撞</b><br />這段期間極易與上級主管發生摩擦或理念衝突。切忌硬碰硬，務必展現高情商，學會以退為進的職場智慧。<br /><br /><b>9月：絕地反攻時刻</b><br />撐過前期的低谷，本月將迎來絕佳轉機。這是你大展才華的關鍵期，請毫不保留地拿出專業實績，一舉奪回主導權。</p>",
    advice:
      "<p><b>1. 穩住底線，把絆腳石當作高壓教練</b><br />今年最大的勝利叫做「安然存活」。面對帶來極大壓力的主管或難纏對手，與其硬碰硬，不如把他們當成磨練你的教練。先保住現有的職位與基本盤，再暗中觀察並學習他們的策略，讓這些壓力直接變成你大躍進的催化劑。<br /><br /><b>2. 聰明借力，遇到困難絕不單打獨鬥</b><br />遇到扛不住的挑戰時，千萬不要自己關起門來死撐。請主動向有經驗的資深前輩請教；遇到難題立刻向上級回報，善用公司的制度與資源來保護自己。懂得向上管理，讓這些強大的「靠山」來幫你分擔火力。<br /><br /><b>3. 遠離口水戰，用絕對的實績說話</b><br />在複雜的權力鬥爭與人事糾紛中，爭辯是最無力的。請把所有的精力都集中在你的專業上。只要你能端出無可挑剔、讓所有人都閉嘴的亮眼成果，這就是你平息一切質疑、建立自身權威的最強大武器。</p>",
  },
  money: {
    degree: 1,
    imagery: "以技換糧，勞而有獲",
    content:
      "<p>今年的財運走勢非常明確：這絕對不是一個能靠運氣、偏財或輕鬆投資來獲利的年份。你今年的收入多寡，將完全與你的「專業硬實力」與「勞心勞力」緊緊綁在一起。令人振奮的是，你的創意、才華與專業技術正處於能量最旺盛、最活躍的巔峰狀態。這意味著，你腦中的點子、你的產出與你的手藝，就是你今年最強大的印鈔機，每一分進帳都將是你憑真本事扎扎實實賺出來的。<br /><br />然而，賺錢的過程同時也伴隨著極大的消耗。隨著事業上面臨的高壓與挑戰，你今年在財務上最大的危機在於「守不住財」。各種突發狀況的處理費、難以避免的開銷，甚至是為了抒解龐大壓力而產生的報復性消費，都會讓你的荷包迅速失血。更需要警惕的是，過度透支體力所引發的「健康問題」，極可能成為你今年最龐大的一筆意外支出，讓好不容易累積的積蓄瞬間流失。<br /><br />因此，今年在財務管理上必須保持高度的清醒與務實。這是一場「以技換糧」的硬仗，每一塊錢都沾著你的汗水與心力。不要去幻想一夕致富或投機獲利的捷徑，請把所有的心思都投入在提升自我價值與專業產出上。同時，務必嚴格控管現金流，為突發事件預留足夠的緊急預備金。把身體照顧好、把錢花在刀口上，穩紮穩打地守住你辛苦耕耘得來的每一分收穫，就是今年最成功的理財戰略。</p>",
    monthHint:
      "<p><b>4月與12月：警惕人情破財</b><br />這兩個月是財務失血的高風險期，極容易因為人情壓力、無效的社交應酬或是借貸而大傷荷包。請務必踩穩底線，學會勇敢談錢與說不。<br /><br /><b>9月：技能變現黃金期</b><br />本月將迎來絕佳的獲利轉機。你累積的專業才華與創意將進入豐收階段，請好好把握機會，將你的硬實力直接轉化為豐厚的實質收入。</p>",
    advice:
      "<p><b>1. 打磨專業，讓你的手藝成為印鈔機</b><br />今年你的收入高度完全取決於你的「專業深度」。請將所有精力放在提升核心技能上，無論是設計、諮詢還是任何技術領域，請把它們打磨到極致。記住，今年是靠實力換取財富的年份，你的硬實力就是你最強大的現金流來源。<br /><br /><b>2. 建立防護罩，遠離高風險投機</b><br />全年的財運利於「勞動所得」而非「投機獲利」。請務必遠離股票、加密貨幣等高風險投資，避免因為一時衝動的錯誤決策導致巨大損失。同時，一定要建立一筆「應急儲備金」，專門用來應對突發狀況，確保你的生活節奏不會因為意外開銷而失控。<br /><br /><b>3. 精準投資，守住最核心的身體資產</b><br />今年最大的財富就是你的健康。與其把錢花在無謂的消費上，不如投資在健身、體檢與高品質的飲食上。請記住，這不是在花錢，而是在保護你今年最重要的「生財工具」。只要身體撐得住，你的專業產出就不會斷，這才是今年最划算的長線投資。</p>",
  },
  love: {
    degree: 1,
    imagery: "獨行的旅人，無暇他顧",
    content:
      "<p>今年的感情生活注定會被推向邊緣，因為你的生存與戰鬥才是全年的第一主題。在高壓環境下，你常會感到心力交瘁，甚至連照顧好自己都顯得吃力，很難再擠出多餘的社交精力去經營一段細膩、浪漫的關係。對你而言，這是一個需要先學會「自我修復」而非「向外索求」的年份。<br /><br />對於單身的你來說，今年雖然有機會遇到對象，但對方往往帶給你巨大的壓力，或是需要你不斷單方面付出。這種關係通常短暫且令人精疲力竭，與其在這種消磨中受挫，不如先把重心拉回自己身上，把自己照顧好才是最穩定的投資。<br /><br />對於有伴侶的你，今年最大的挑戰在於如何「不把情緒帶回家」。事業上的高壓極易轉化為對另一半的不耐煩，甚至引發無謂的爭吵。對方會明顯感受到你的疏離與緊繃，這讓關係很容易陷入一段低溫冰凍期。請務必提醒自己，伴侶是你的隊友而非情緒的出口，適時的溝通與脆弱的分享，才能避免關係在風暴中失溫。</p>",
    monthHint:
      "<p><b>4月與12月：防範外力介入與競爭</b><br />這兩個月份在情感與社交圈中容易出現干擾因素。單身者需留意競爭者的出現，有對象者則要小心身邊朋友的意見或外界雜音影響了你與伴侶的信任感。保持適度的警覺與界線，是這段時間的必修課。<br /><br /><b>8月：言辭火藥味重，溝通需謹慎</b><br />這是一個極易「出口傷人」的月份。由於事業壓力大，你的言論會變得格外尖銳，不經意的一句話就可能重傷感情。在開口爭論前，請先給自己三秒鐘的緩衝，避免因一時的情緒性發言，造成難以修補的裂痕。</p>",
    advice:
      "<p><b>1. 降低期待，先學會與自己談戀愛</b><br />今年請將「自我關懷」放在首位，把自己視為最重要的伴侶。無論單身或有伴，都要對感情生活降低期待，不強求、不幻想，是保護自己不心碎的最好方式。當你能先安撫好自己的疲憊、穩定內心的秩序，才會有餘裕去處理人際關係。<br /><br /><b>2. 坦誠示弱，用溝通替代冷戰</b><br />對於有伴侶的人，與其獨自消化事業高壓而變得陰晴不定，不如坦誠地向對方揭露你的困境與疲憊。請明確告訴對方你需要的只是理解與陪伴，而非要求對方替你解決問題。適度的「示弱」能化解對方的疏離感，讓伴侶成為支撐你的隊友而非壓力來源。<br /><br /><b>3. 預留空間，距離是最好的保護色</b><br />在壓力爆棚的年份，請為自己與對方劃定透明的「獨處空間」。有時候，短暫的抽離與安靜的距離，反而是保護關係不被情緒流彈擊傷的必要手段。給彼此留一點呼吸的縫隙，能有效避免在緊繃狀態下產生不必要的摩擦與消耗。</p>",
  },
  health: {
    degree: 2,
    imagery: "緊繃的弓弦，強制休養",
    content:
      "<p><b>這是你 2026 年最需要嚴肅看待、絕對不能掉以輕心的一章。</b><br />你今年的身體狀態，就像是一根被拉扯到極限、隨時可能斷裂的弓弦。在命理的高壓環境下，你的體力與防禦機制正遭受前所未有的直接打擊，這是一個極度消耗、甚至帶有風險的信號。這意味著，任何看似微小的額外壓力或熬夜，都可能成為壓垮駱駝的最後一根稻草。<br /><br />特別需要警惕的是，今年的健康問題往往不按牌理出牌。它不會慢慢累積，而是呈現出「突發性」與「攻擊性」的特點——可能是一場突如其來的高燒、急性發炎，或是長期累積的疲勞在一瞬間徹底潰堤。你沒有任何揮霍健康的本錢，請務必把身體的求救訊號放在所有事情的首位。<br /><br />這是一場關乎生存意志的耐力賽，而非百米衝刺。在面對事業與財運的狂風暴雨時，請記住，你的身體是你唯一的本錢，也是最後的底線。一旦這道防線失守，其餘的成就都將失去意義。請將今年的節奏放慢，學會聽從身體的節奏，而不是盲目地透支。唯有保住健康，你才能在風暴過後看見彩虹。</p>",
    monthHint:
      "<p><b>3月：身體防禦的極限挑戰</b><br />這是全年健康最凶險的時刻。突發性的壓力會直接衝擊你的體質，各種小病痛極易在此時演變成嚴重的健康危機。請務必推掉不必要的應酬，保證充足的睡眠，這是一場絕對不能輸的體力保衛戰。<br /><br /><b>8月：提防心力交瘁的透支感</b><br />本月你的人體能量正處於高度流失狀態。除了體力活，更多的是「勞心」帶來的精神耗損。你可能會感到莫名疲倦、專注力下降或情緒緊繃，請強制自己停下來喘息，避免因過度透支而引發身體的全面抗議。</p>",
    advice:
      "<p><b>1. 睡眠即是戰力，絕不妥協的修復期</b><br />今年請將「充足的睡眠」提升到與工作同等、甚至更高的高度。你的身體正處於極度消耗的狀態，睡眠是你唯一的自我修復機會。請強制自己每天準時關機，這不是在偷懶，而是為了讓瀕臨崩潰的防線重新補給。沒有睡眠，就沒有戰鬥力。<br /><br /><b>2. 建立雷打不動的規律作息</b><br />規律是今年最強大的防禦工事。請建立起最穩定的生活節奏，無論是飲食還是作息，都要像齒輪一樣準確運行。同時，運動請採取「動靜結合」的策略，避開高強度、挑戰極限的劇烈運動，改以散步、瑜伽或拉伸等緩和方式來保持氣血循環，避免過度耗損元氣。<br /><br /><b>3. 預防勝於治療，即刻啟動健康檢查</b><br />請在年初就安排一次全面的身體檢查，對自己的現狀有清晰的掌握。今年不容許任何「拖延症」，只要身體發出微小的求救訊號，請第一時間處理。健康是你走過這場高壓淬鍊之旅唯一的本錢，守住這道底線，你才有機會贏得最後的勝利。</p>",
  },
  family: {
    degree: 1,
    imagery: "溫暖的壁爐，最終港灣",
    content:
      "<p>今年的家庭是你最強大的後台，也是你唯一的避風港。當你在外界的競爭與高壓中感到精疲力竭時，回到家中，那份與生俱來的安全感將會成為你重新站起來的力量。這股溫暖的能量紮根於你的生活深處，代表著家庭與長輩是你今年對抗所有外部挑戰的核心支柱。無論外頭的風雪多麼寒冷，家人的理解與支持，永遠是你最後一道、也是最堅固的防線。<br /><br />在今年這種充滿戰鬥感的節奏中，你會發現自己比以往任何時候都更加依賴家庭。這並非弱點，而是一種聰明的「能量補給」。來自長輩的叮嚀、與家人共處的平靜時光，甚至是處理家中瑣事時的踏實感，都能幫你卸下武裝、療癒受傷的元氣。請珍惜這份無需偽裝的親情，因為它正是支撐你今年能夠在事業與財運中橫衝直撞、而不至於徹底崩潰的最重要動能。</p>",
    monthHint:
      "<p><b>5月與10月：家庭能量的巔峰期</b><br />這兩個月份是你與家人、長輩深度連結的最佳時機。無論是尋求情感上的慰藉，還是現實生活中的實質支援，這段時間家人的包容力與支撐力都最強。請多安排與長輩聚會或回家吃飯，這將為你枯竭的身心注入最強大的回血能量。</p>",
    advice:
      "<p><b>1. 放下驕傲，坦然接受家人的支撐</b><br />當你在外衝鋒陷陣感到疲憊，而家人主動伸出援手時，請收起你的好強，大方接受這份支持。這並非軟弱，而是一種聰明的戰略補給。家人的智慧與無條件的包容，是你今年最寶貴、也最無可取代的隱形資源，能讓你更有底氣去打外面的硬仗。<br /><br /><b>2. 適度傾訴，讓家成為情緒的淨化站</b><br />今年請練習「報喜也報憂」，不要試圖把所有壓力都獨自扛下。適度地與父母或長輩分享你的困境與疲累，他們的傾聽本身就帶有強大的療癒力量。透過溝通與相處，你會發現那些在外頭看似無解的難題，在溫暖的親情面前都會變得不再那麼面目可憎。<br /><br /><b>3. 營造安穩環境，打造專屬的療癒空間</b><br />請務必保持居住環境的整潔與舒適，讓家真正發揮「避風港」的功能。一個安穩、有序且充滿生活感的居家環境，能極大地撫平你在外受創的身心。當你踏進家門的那一刻，能感受到全然的放鬆與寧靜，這就是你迎接明天挑戰時最強大的防禦工事。</p>",
  },
  social: {
    degree: 3,
    imagery: "黑白棋局，辨敵識友",
    content:
      "<p>今年你的人際關係將會經歷一場劇烈的「大洗牌」，所有的偽裝都將消失，關係會變得極度簡化且黑白分明。那些隱藏在暗處的「對手」或對你有敵意的人，受環境壓力的催化，將會帶著清晰的攻擊性直接浮出水面。你不再需要花心思去猜測誰是敵是友，因為現實的考驗會逼著所有人表態，讓對方的立場變得一目了然。<br /><br />然而，這場風暴同時也是檢驗真心的試金石。正因為外在環境惡劣，那些願意在你危難時刻挺身而出、為你擋下流言蜚語的「長輩」與「貴人」，會顯得格外耀眼且真實。他們無條件的支持與實質的提攜，將是你今年在人際叢林中最強大的護身符。<br /><br />這是一個「提純」的過程：雖然你會感受到直接的挑戰與衝撞，但那些留下來的，都將是真正值得你深交的核心人脈。請保持清醒與冷靜，勇敢面對那些公開的競爭，同時加倍珍惜那些在風雨中為你撐傘的人。今年的人際攻略很簡單：遠離消耗你的爛人，靠近能拉你一把的貴人，就這麼直截了當。</p>",
    monthHint:
      "<p><b>4月與12月：留意同儕間的競爭火藥味</b><br />這兩個月份在職場或平輩圈中，容易出現資源爭奪或比較的心態。請保持低調並守好自己的成果，避免捲入無謂的口舌之爭，防範競爭者在背後的小動作。<br /><br /><b>5月與10月：貴人加持的黃金社交期</b><br />這是你今年人脈能量最強、最容易獲得實質提攜的時刻。請務必主動與前輩、長輩或有影響力的人物聯繫。不管是請教問題還是分享近況，這段時間的人際連結將為你帶來意想不到的轉機與強大的後援。</p>",
    advice:
      "<p><b>1. 戰略性疏離，不與負能量糾纏</b><br />今年最好的對抗就是「遠離」。面對那些不斷消耗你、製造麻煩的小人或競爭者，請不要浪費任何一絲精力去爭辯或反擊。將你的專注力抽離，讓他們在你的世界中失效，就是最高明的防禦手段。把有限的能量，留給真正重要的戰場。<br /><br /><b>2. 鎖定生命線，主動維繫核心貴人</b><br />請清晰地辨別出那些在你陷入困境時，依然願意給予支持的上司、長輩或朋友。他們是你今年最重要的支撐力量，也是你人脈中的「生命線」。請主動靠近、保持聯繫，並以最真誠的態度回饋這份提攜，讓這股正向的貴人能量形成良性循環。<br /><br /><b>3. 拒絕無效社交，精簡你的朋友圈</b><br />這是一個需要「人際斷捨離」的年份。請果斷謝絕所有無意義的飯局與虛假社交，那些只會消耗時間卻無實質幫助的聚會，只會分散你的戰鬥力。請把所有的社交配額，精準地留給最關鍵的盟友與值得深交的夥伴，讓每一分社交投入都產生真實的價值。</p>",
  },
  monthAnalyze: {
    title: "淬火工坊的十二道工序",
    months: [
      {
        month: 1,
        date: "2026/01/05~2026/02/04",
        tenGod: "正官",
        title: "按部就班約束求穩",
        situation:
          "本月受限於體制或上級，行事須遵循規則，雖有束縛感但局勢可控，不越界即無大礙。",
        instruction:
          "不宜強出頭，請謹言慎行、遵守遊戲規則，這是風暴前珍貴的平靜期。",
      },
      {
        month: 2,
        date: "2026/02/05~2026/03/05",
        tenGod: "七殺",
        title: "壓力警報強衝擊",
        situation:
          "年、月壓力疊加，整體壓迫感達頂峰，難題、小人與健康隱憂可能集中爆發，極度考驗韌性。",
        instruction:
          "低頭忍耐、切勿硬碰硬，尋求庇護並保存實力，穩住陣腳才能渡過風暴。",
      },
      {
        month: 3,
        date: "2026/03/06~2026/04/05",
        tenGod: "劫財",
        title: "人際考驗防破財",
        situation:
          "本月面臨同輩競爭與人情壓力，穩固關係恐生裂痕，易因團隊或朋友導致金錢損失與口舌紛爭。",
        instruction:
          "守緊錢包、看清盟友，不輕易承諾借貸，保持社交距離冷眼旁觀。",
      },
      {
        month: 4,
        date: "2026/04/06~2026/05/05",
        tenGod: "劫財",
        title: "轉捩點最強後援",
        situation:
          "年、月貴人能量疊加，支援體系達頂峰，棘手危機有望化解，長輩與上級的幫助將及時到位。",
        instruction:
          "放下身段主動求助、真心感恩援手，把這份支持轉化為實質防線。",
      },
      {
        month: 5,
        date: "2026/05/06~2026/06/05",
        tenGod: "偏印",
        title: "靈光乍現磨直覺",
        situation:
          "本月思維活躍、直覺敏銳，能看穿表象並接觸非主流靈感，雖易感孤獨卻最適合靜心學習。",
        instruction: "相信直覺但不宜做重大決策，保持學習並警惕不切實際的幻想。",
      },
      {
        month: 6,
        date: "2026/06/06~2026/07/07",
        tenGod: "比肩",
        title: "獨立應戰靠自己",
        situation:
          "本月需獨自衝鋒，外界助力有限，甚至合作者恐轉向競爭，是考驗實力與決策的關鍵時刻。",
        instruction: "凡事親力親為、收回對外依賴，專注鞏固陣地與核心利益。",
      },
      {
        month: 7,
        date: "2026/07/08~2026/08/07",
        tenGod: "傷官",
        title: "才華橫溢慎衝撞",
        situation:
          "思維口才達巔峰，才華盡顯卻伴隨強烈叛逆，對規則不耐，易與上級或體制正面對立。",
        instruction: "才華用於解決問題而非製造對立，收斂鋒芒、話留三分。",
      },
      {
        month: 8,
        date: "2026/08/08~2026/09/07",
        tenGod: "食神",
        title: "才華盛放舒壓窗口",
        situation:
          "本月是展現專業、打磨作品的高光期，透過實績可贏得讚賞，也是連月高壓後難得的喘息之機。",
        instruction:
          "將精力投入專業細節、全神貫注享受過程，讓成果成為最有力的證明。",
      },
      {
        month: 9,
        date: "2026/09/08~2026/10/08",
        tenGod: "正印",
        title: "貴人降臨局勢反轉",
        situation:
          "前三季動盪結束，本月強大支撐進場，是掃除障礙、扭轉頹勢的黃金轉捩點，難題將迎刃而解。",
        instruction:
          "放下身段連結長輩與上級，大方尋求資源與指引，讓貴人成為攻堅後盾。",
      },
      {
        month: 10,
        date: "2026/10/08~2026/11/07",
        tenGod: "偏印",
        title: "深度自省靈感湧現",
        situation:
          "風暴暫歇，重心轉向自我探索，直覺敏銳，適合學習研究，雖感孤獨卻是轉化智慧的養料。",
        instruction:
          "遠離喧囂社交、給心靈沉澱空間，核心答案就藏在你的洞察力之中。",
      },
      {
        month: 11,
        date: "2026/11/08~2026/12/07",
        tenGod: "劫財",
        title: "利益重分配防流失",
        situation:
          "風暴後焦點轉向內部分配，合作增加卻伴隨破財風險，易因人情導致損失，考驗金錢邊界。",
        instruction: "利益劃分須冷靜、堅持論功行賞，守住核心利益與錢包。",
      },
      {
        month: 12,
        date: "2026/12/08~2027/01/05",
        tenGod: "比肩",
        title: "獨立應戰檢驗實力",
        situation:
          "本月需獨自承擔與衝鋒，外界助力極限，合作對象恐轉向競爭，是考驗獨立作戰的關鍵月。",
        instruction: "凡事親力親為、收回對外依賴，專注鞏固陣地展現自主性。",
      },
    ],
  },
  conclusion:
    "<p>當你讀到這裡，想必已經預見了 2025 年這場「淬火之旅」的壯闊與不易。這注定不是輕鬆的一年，命運會推著你走出舒適圈，逼你燃燒潛能，去直面那些最深刻的挑戰與抉擇。<br /><br />但請務必記住，淬火的目的從來不是為了讓你受苦，而是在烈火與冷水的極端磨練中，將平凡的鐵胚鍛造成一把削鐵如泥的寶劍。今年你所淬煉的，不僅是應對危機的手段，更是你的意志、智慧，以及足以承擔更高權力的肩膀。<br /><br />當你走過這一年回頭望去，你會驚訝地發現，自己已經站上了過去不敢想像的高度，擁有一顆更強大、也更堅韌的核心。元辰雲閣將是你這一年最忠實的守望者，祝福你在這場華麗的冒險中，不僅淬鍊成器，更鍊就屬於你的耀眼光芒。</p>",
};

// 六大面向假資料
//六大面向雷達圖資料（依當前 data 的 summary.field 算出）
const buildChartData = (data) => [
  { label: "事業", value: data.summary.field.career.score, full: 5 },
  { label: "財運", value: data.summary.field.money.score, full: 5 },
  { label: "愛情", value: data.summary.field.love.score, full: 5 },
  { label: "人際", value: data.summary.field.social.score, full: 5 },
  { label: "家庭", value: data.summary.field.family.score, full: 5 },
  { label: "健康", value: data.summary.field.health.score, full: 5 },
];

const StarRating = ({ score }) => (
  <div className="stars">
    {Array.from({ length: score }).map((_, i) => (
      <span key={i}>★</span>
    ))}
    {Array.from({ length: 5 - score }).map((_, i) => (
      <span key={i}>☆</span>
    ))}
  </div>
);

const MonthGrid = ({ months }) => (
  <div className="month-grid">
    <div className="month-header">
      <span className="header-cell v-cell">月份</span>
      <span className="header-cell v-cell">十神</span>
      <span className="header-cell">核心情境與行動指令</span>
    </div>
    {months.map((item, index) => (
      <div className="month-row" key={index}>
        <div className="cell-month">
          <span className="month">{item.month}</span>
          <span className="month">月</span>
        </div>
        <div className="cell-god">
          <span className="god-badge">{item.tenGod}</span>
        </div>
        <div className="cell-content">
          <span className="date">🗓️ {item.date}</span>
          {item.content ? (
            // 舊報告：content 為一整段 HTML
            <div
              className="rich-text"
              dangerouslySetInnerHTML={{ __html: item.content }}
            />
          ) : (
            // 新報告：title / situation / instruction 三欄組裝
            <div className="rich-text">
              <p>
                <b>{item.title}</b>：{item.situation}
                <br />
                <b>【指令】</b>：{item.instruction}
              </p>
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
);

const buildFieldSection = (key, title, fieldData) => ({
  key,
  title,
  pages: [
    {
      id: `${key}-1`,
      content: (
        <div className="page-content">
          <div className="field">
            <div className="field-title">
              <span>{title} —— </span>
              <span>{fieldData.imagery}</span>
            </div>
            <div className="field-holder">
              {/* 封面圖由後端給完整 URL（Firebase Storage）；
                  取圖失敗才退回本地圖，避免整頁破版
                  ⚠️ key 一定要帶：六個面向的 <img> 在同一個結構位置，
                     沒 key 時 React 會重用同一個 DOM 節點、只換 src，
                     瀏覽器就會「繼續顯示上一張已載入的圖」直到新圖下載完
                     （圖 ~1MB 從 Storage 拉要數秒）→ 出現點健康卻先看到愛情封面 */}
              <img
                key={key}
                src={
                  fieldData.cover ||
                  `/images/bg-${key}-field-${[1, 2, 3].includes(fieldData.degree) ? fieldData.degree : 1}.png`
                }
                alt=""
              ></img>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: `${key}-2`,
      content: (
        <div className="page-content">
          <div className="chapter-title">
            <div className="chapter-label"></div>
            <h2>流年狀況解析</h2>
          </div>
          <div dangerouslySetInnerHTML={{ __html: fieldData.content }}></div>
        </div>
      ),
    },
    {
      id: `${key}-3`,
      content: (
        <div className="page-content">
          <div className="chapter-title">
            <div className="chapter-label"></div>
            <h2>流月提示</h2>
          </div>
          <div
            className="hint"
            dangerouslySetInnerHTML={{ __html: fieldData.monthHint }}
          ></div>
        </div>
      ),
    },
    {
      id: `${key}-4`,
      content: (
        <div className="page-content">
          <div className="chapter-title">
            <div className="chapter-label"></div>
            <h2>流年建議</h2>
          </div>
          <div dangerouslySetInnerHTML={{ __html: fieldData.advice }}></div>
        </div>
      ),
    },
  ],
});

const FlowYearReportComponent = ({
  data: propData,
  setShowServiceMenu,
  setShowPersonalMenu,
}) => {
  //付款流程透過 navigate("/flow-report", { state: { data } }) 帶資料進來；
  //後台「查看產品」則由 propData 直接帶入；皆無時退回 mockData 供本地預覽
  const location = useLocation();
  const data = propData ?? location.state?.data ?? mockData;
  //console.log("[FlowYearReport] data：", JSON.stringify(data, null, 2));
  const chartData = useMemo(() => buildChartData(data), [data]);

  //預先載入六大面向封面圖：圖存在 Firebase Storage、每張約 1MB，
  //  等翻到該頁才下載會空白數秒。報告一進來就在背景抓進瀏覽器快取，翻頁即瞬顯。
  useEffect(() => {
    const covers = ["career", "money", "love", "health", "family", "social"]
      .map((k) => data?.[k]?.cover)
      .filter(Boolean);
    covers.forEach((src) => {
      const img = new Image();
      img.src = src; //只為觸發下載進快取，不需掛到畫面
    });
  }, [data]);

  //iPad 直式也走 web JSX：避免 mobile 雙頁 trick 在大平板上錯位、且資料頁設計要跟 web 一樣
  const isMobile = useIsBookPhone();
  const [viewStartIndex, setViewStartIndex] = useState(0);
  const [activeSection, setActiveSection] = useState("cover");
  const [navHeight, setNavHeight] = useState(0);

  // 翻頁動畫狀態管理
  const [flipState, setFlipState] = useState({
    isFlipping: false,
    direction: null,
    oldLeft: null,
    oldRight: null,
    oldMobile: null,
  });

  useEffect(() => {
    //量 nav 高度（推報告下移，避免被固定 nav 蓋住）
    //  使用 retry：nav 在某些 timing 下 useEffect 跑時還沒 mount，量不到會卡 0
    const measure = () => {
      const nav = document.querySelector(
        isMobile ? ".mobile-dashboard nav" : ".dashboard nav",
      );
      if (nav) setNavHeight(nav.offsetHeight);
    };
    measure();
    const t1 = setTimeout(measure, 50);
    const t2 = setTimeout(measure, 200);
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", measure);
    };
  }, [isMobile]);
  const pagesPerView = isMobile ? 1 : 2;

  // 章節資料
  const sections = useMemo(
    () => [
      {
        key: "cover",
        title: "封面",
        hideTab: true,
        pages: [
          {
            id: "cover-page",
            content: (
              <div className="cover">
                <img src="/images/bg-flow-report.png"></img>
                <div className="cover-info">
                  <span className="name">{data.name}</span>
                  <div className="years">
                    <div className="year">
                      <span>{data.ganShiYear}年</span>
                      <div className="divide"></div>
                      <span>
                        <b>{data.year}</b>
                      </span>
                    </div>
                    <span className="label">流年之書</span>
                  </div>
                </div>
              </div>
            ),
          },
        ],
      },
      {
        key: "summary",
        title: "總論",
        pages: [
          {
            id: "summary-1",
            content: (
              <div className="page-content">
                <h1>{data.summary.preface.title}</h1>
                <p className="preface">{`【序：致${data.name}】`}</p>
                <div
                  dangerouslySetInnerHTML={{
                    __html: data.summary.preface.content,
                  }}
                ></div>
              </div>
            ),
          },
          {
            id: "summary-2",
            content: (
              <div className="page-content">
                {/* 大運在上：定位讀者目前的階段（大運屬總論，收在 summary 內） */}
                {data.summary?.daYun && (
                  <>
                    <div className="chapter-title">
                      <div className="chapter-label"></div>
                      <h2>{`你目前走的大運：${data.summary.daYun.ganZhi}運${data.summary.daYun.half}，由${data.summary.daYun.tenGod}主導`}</h2>
                    </div>
                    <p>{data.summary.daYun.situation}</p>
                    <p>{data.summary.daYun.direction}</p>
                    <div className="dayun-divider"></div>
                  </>
                )}

                {/* 回顧在下 */}
                <div className="chapter-title">
                  <div className="chapter-label"></div>
                  <h2>{`回顧 ${data.year - 1} —— ${data.summary.review.title}`}</h2>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: data.summary.review.content,
                  }}
                ></div>
              </div>
            ),
          },
          {
            id: "summary-3",
            content: (
              <div className="page-content">
                <div className="chapter-title">
                  <div className="chapter-label"></div>
                  <h2>今年度題字</h2>
                </div>
                <div className="inscription">
                  <img src="/images/bg-inscription.png"></img>
                  <span>{data.summary.inscription.word}</span>
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: data.summary.inscription.simple,
                  }}
                ></div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: data.summary.inscription.challenge,
                  }}
                ></div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: data.summary.inscription.method,
                  }}
                ></div>
              </div>
            ),
          },
          {
            id: "summary-4",
            content: (
              <div className="page-content">
                <div className="chapter-title">
                  <div className="chapter-label"></div>
                  <h2>年度六大面向總覽</h2>
                </div>

                <RadarChart
                  width={230}
                  height={230}
                  data={chartData}
                  outerRadius="75%"
                  margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                  startAngle={90}
                  endAngle={-270}
                  style={{ pointerEvents: "none" }}
                >
                  <PolarGrid
                    gridType="polygon"
                    stroke="#c6c6c6"
                    strokeDasharray="5 5"
                  />
                  <PolarAngleAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#615557", fontSize: 14, fontWeight: 600 }}
                  />
                  <PolarRadiusAxis
                    domain={[0, 5]}
                    tickCount={6}
                    tick={false}
                    axisLine={false}
                  />
                  <Radar
                    dataKey="full"
                    stroke="#808080"
                    strokeWidth={2.5}
                    fill="none"
                    dot={false}
                    isAnimationActive={false}
                  />
                  <Radar
                    dataKey="value"
                    stroke="none"
                    fill="#dcb36d"
                    fillOpacity={0.85}
                    isAnimationActive={true}
                  />
                </RadarChart>

                {[
                  ["事業", data.summary.field.career],
                  ["財運", data.summary.field.money],
                ].map(([label, field]) => (
                  <div className="item-field" key={label}>
                    <div className="degree">
                      <span className="label">●</span>
                      <span>{label}</span>
                      <StarRating score={field.score} />
                    </div>
                    {field.content ? (
                      // 舊報告：整段 HTML content
                      <div
                        dangerouslySetInnerHTML={{ __html: field.content }}
                      ></div>
                    ) : (
                      // 新報告：title + desc 兩欄組裝
                      <p>
                        <b>{field.title}</b> {field.desc}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ),
          },
          {
            id: "summary-5",
            content: (
              <div className="page-content">
                {[
                  ["愛情", data.summary.field.love],
                  ["人際", data.summary.field.social],
                  ["家庭", data.summary.field.family],
                  ["健康", data.summary.field.health],
                ].map(([label, field]) => (
                  <div className="item-field" key={label}>
                    <div className="degree">
                      <span className="label">●</span>
                      <span>{label}</span>
                      <StarRating score={field.score} />
                    </div>
                    {field.content ? (
                      // 舊報告：整段 HTML content
                      <div
                        dangerouslySetInnerHTML={{ __html: field.content }}
                      ></div>
                    ) : (
                      // 新報告：title + desc 兩欄組裝
                      <p>
                        <b>{field.title}</b> {field.desc}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ),
          },
        ],
      },
      buildFieldSection("career", "事業", data.career),
      buildFieldSection("money", "財運", data.money),
      buildFieldSection("love", "愛情", data.love),
      buildFieldSection("health", "健康", data.health),
      buildFieldSection("family", "家庭", data.family),
      buildFieldSection("social", "人際", data.social),
      {
        key: "month",
        title: "流月",
        pages: [
          {
            id: "month-1",
            content: (
              <div className="page-content">
                <div className="field">
                  <div className="field-title">
                    <span>流月節奏 —— </span>
                    <span>{data.monthAnalyze.title}</span>
                  </div>
                </div>
                <MonthGrid months={data.monthAnalyze.months.slice(0, 3)} />
              </div>
            ),
          },
          ...[1, 2, 3].map((pageIdx) => ({
            id: `month-${pageIdx + 1}`,
            content: (
              <div className="page-content">
                <MonthGrid
                  months={data.monthAnalyze.months.slice(
                    pageIdx * 3,
                    pageIdx * 3 + 3,
                  )}
                />
              </div>
            ),
          })),
        ],
      },
      {
        key: "conclusion",
        title: "結語",
        pages: [
          {
            id: "conclusion-1",
            content: (
              <div className="page-content">
                <div className="chapter-title">
                  <div className="chapter-label"></div>
                  <h2>結語</h2>
                </div>

                <div className="ending">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: data.conclusion,
                    }}
                  ></div>

                  {/*產品附註*/}
                  <div className="memo">
                    <span className="product-id">{`產品編號 ${data.productID}`}</span>
                    <span className="end">
                      {`本分析由元辰雲閣命理技術團隊提供，為你點亮 ${data.year} 年的指引明燈，願你手握智慧、從容應變，讓每一步挑戰都成就更好的自己。`}
                    </span>
                    <div className="links">
                      <a href={SHARE_URL} onClick={handleShare}>
                        👉 點擊分享{" "}
                      </a>
                      <span>|</span>
                      <a
                        href={LINE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        👉 官方LINE客服{" "}
                      </a>
                      <span>|</span>
                      <Link to={`/consult?order=${data.orderID || ""}`}>👉 專業諮詢 </Link>
                    </div>
                  </div>
                </div>
              </div>
            ),
          },
        ],
      },
    ],
    [],
  );

  const bookPages = useMemo(() => {
    // 1. 攤平所有頁面並加上 meta data
    const allPages = sections.flatMap((section) =>
      section.pages.map((page, pageIndex) => ({
        ...page,
        section: section.key,
        sectionTitle: section.title,
        pageInSection: pageIndex + 1,
        totalPagesInSection: section.pages.length,
        isBlank: false,
      })),
    );

    // 2. 抽離封面與正文
    const coverPage = allPages.find((p) => p.section === "cover");
    const contentPages = allPages.filter((p) => p.section !== "cover");

    // 3. 手機版：最單純的線性結構，不需任何排版佔位頁
    if (isMobile) {
      return [coverPage, ...contentPages];
    }

    // 4. 桌機版：加入 3D 雙頁排版需要的透明頁與空白頁
    return [
      {
        id: "transparent-left",
        section: "cover",
        isBlank: true,
        isTransparent: true,
        content: null,
      }, // Index 0: 透明左頁
      coverPage, // Index 1: 封面實體
      {
        id: "blank-inside-left",
        section: "summary",
        sectionTitle: "總論",
        isBlank: true,
        content: null,
      }, // Index 2: 翻開後的左側留白
      ...contentPages, // Index 3 開始是正文
    ];
  }, [sections, isMobile]);

  const sectionFirstPageMap = useMemo(() => {
    const map = {};
    bookPages.forEach((page, index) => {
      if (!page.isBlank && map[page.section] === undefined) {
        map[page.section] = index;
      }
    });
    return map;
  }, [bookPages]);

  useEffect(() => {
    if (!isMobile && viewStartIndex % 2 !== 0) {
      setViewStartIndex((prev) => prev - 1);
    }
  }, [isMobile, viewStartIndex]);

  const visiblePages = useMemo(() => {
    if (isMobile) {
      // 手機版左頁規則：
      // - 封面（index 0）：透明（灰區乾淨）
      // - 第一個內頁（index 1）：空白頁（封面與內文間的留白）
      // - 其餘內頁：上一頁內容（讓翻過去的頁停在灰色區）
      const currentPage = bookPages[viewStartIndex];
      let leftSlot;
      if (viewStartIndex === 0) {
        leftSlot = {
          id: "mobile-cover-left",
          isBlank: true,
          isTransparent: true,
          content: null,
        };
      } else if (viewStartIndex === 1) {
        leftSlot = {
          id: "mobile-blank-after-cover",
          isBlank: true,
          isTransparent: false,
          content: null,
        };
      } else {
        leftSlot = bookPages[viewStartIndex - 1];
      }
      return [leftSlot, currentPage];
    }
    return bookPages.slice(viewStartIndex, viewStartIndex + 2);
  }, [bookPages, isMobile, viewStartIndex]);

  const leftPage = visiblePages[0];
  const rightPage = visiblePages[1];
  const mobilePage = isMobile ? rightPage : null;

  const currentVisibleIndices = isMobile
    ? [viewStartIndex]
    : [viewStartIndex, viewStartIndex + 1];

  const getDisplaySection = (startIndex) => {
    if (isMobile) {
      const page = bookPages[startIndex];
      if (page?.isBlank) return bookPages[startIndex + 1]?.section || "summary";
      return page?.section || "summary";
    }
    const left = bookPages[startIndex];
    const right = bookPages[startIndex + 1];
    if (left?.isBlank) return right?.section || "summary";
    return left?.section || right?.section || "summary";
  };

  // 核心翻頁觸發器
  const triggerFlip = (newIndex, direction, sectionKey = null) => {
    if (flipState.isFlipping) return;

    setFlipState({
      isFlipping: true,
      direction,
      oldLeft: leftPage,
      oldRight: rightPage,
      oldMobile: mobilePage,
    });

    setViewStartIndex(newIndex);
    setActiveSection(sectionKey || getDisplaySection(newIndex));

    // 800ms 要與 SCSS 內的動畫時間對齊
    setTimeout(() => {
      setFlipState({
        isFlipping: false,
        direction: null,
        oldLeft: null,
        oldRight: null,
        oldMobile: null,
      });
    }, 800);
  };

  const handlePrevPage = () => {
    if (flipState.isFlipping) return;
    const prevIndex = viewStartIndex - pagesPerView;
    if (prevIndex < 0) return;
    triggerFlip(prevIndex, "prev");
  };

  const handleNextPage = () => {
    if (flipState.isFlipping) return;
    const nextIndex = viewStartIndex + pagesPerView;
    if (nextIndex >= bookPages.length) return;
    triggerFlip(nextIndex, "next");
  };

  const handleTabClick = (sectionKey) => {
    if (flipState.isFlipping) return;
    const targetIndex = sectionFirstPageMap[sectionKey];
    if (targetIndex === undefined) return;

    if (isMobile) {
      if (targetIndex === viewStartIndex) {
        setActiveSection(sectionKey);
        return;
      }
      const direction = targetIndex > viewStartIndex ? "next" : "prev";
      triggerFlip(targetIndex, direction, sectionKey);
      return;
    }

    const alignedIndex = targetIndex % 2 === 0 ? targetIndex : targetIndex - 1;
    if (currentVisibleIndices.includes(targetIndex)) {
      setActiveSection(sectionKey);
      return;
    }

    const direction = alignedIndex > viewStartIndex ? "next" : "prev";
    triggerFlip(alignedIndex, direction, sectionKey);
  };

  const isPrevDisabled = viewStartIndex === 0;
  const isNextDisabled =
    viewStartIndex + pagesPerView >= bookPages.length ||
    (isMobile &&
      bookPages.slice(viewStartIndex + 1).every((page) => page.isBlank));

  // 決定動畫期間底層顯示的靜態內容（桌機）
  const staticLeftPage = flipState.isFlipping
    ? flipState.direction === "next"
      ? flipState.oldLeft
      : leftPage
    : leftPage;

  const staticRightPage = flipState.isFlipping
    ? flipState.direction === "next"
      ? rightPage
      : flipState.oldRight
    : rightPage;

  const tabButtons = sections
    .filter((s) => !s.hideTab)
    .map((section) => (
      <button
        key={section.key}
        type="button"
        className={`tab-item ${activeSection === section.key ? "active" : ""}`}
        onClick={() => handleTabClick(section.key)}
      >
        {section.title}
      </button>
    ));

  const sharedFooter = (
    <SiteFooter />
  );

  return (
    <div className="flow-year-report-page">
      {!isMobile && (
        <main
          className="web-main"
          style={{ paddingTop: navHeight }}
          onMouseEnter={() => {
            if (setShowServiceMenu) setShowServiceMenu(false);
            if (setShowPersonalMenu) setShowPersonalMenu(false);
          }}
        >
          <div className="flow-year-main">
            <div className="selector prev" onClick={handlePrevPage}>
              <img
                src={
                  isPrevDisabled
                    ? "/images/ic-non-select-left.png"
                    : "/images/ic-select-left.png"
                }
                alt="上一頁"
              />
            </div>

            <div className="display">
              <div className="tabs">{tabButtons}</div>

              <div className="container">
                <div
                  className={`book desktop-book ${staticLeftPage?.isTransparent ? "is-cover" : ""}`}
                >
                  {/* 桌機版：靜態左頁 */}
                  <div
                    className={`book-page left-page ${staticLeftPage?.isTransparent ? "transparent-page" : ""}`}
                  >
                    <div className="page-inner">
                      {!staticLeftPage?.isBlank && staticLeftPage?.content}
                    </div>
                  </div>

                  {/* 桌機版：靜態右頁 */}
                  <div className="book-page right-page">
                    <div className="page-inner">
                      {!staticRightPage?.isBlank && staticRightPage?.content}
                    </div>
                  </div>

                  {/* 桌機版：往右翻 (下一頁) */}
                  {flipState.isFlipping && flipState.direction === "next" && (
                    <div className="flip-layer flip-next">
                      <div className="flip-front book-page">
                        <div className="page-inner">
                          {!flipState.oldRight?.isBlank &&
                            flipState.oldRight?.content}
                        </div>
                      </div>
                      <div className="flip-back book-page">
                        <div className="page-inner">
                          {!leftPage?.isBlank && leftPage?.content}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 桌機版：往左翻 (上一頁) */}
                  {flipState.isFlipping && flipState.direction === "prev" && (
                    <div className="flip-layer flip-prev">
                      <div className="flip-front book-page">
                        <div className="page-inner">
                          {!flipState.oldLeft?.isBlank &&
                            flipState.oldLeft?.content}
                        </div>
                      </div>
                      <div className="flip-back book-page">
                        <div className="page-inner">
                          {!rightPage?.isBlank && rightPage?.content}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="selector next" onClick={handleNextPage}>
              <img
                src={
                  isNextDisabled
                    ? "/images/ic-non-select-right.png"
                    : "/images/ic-select-right.png"
                }
                alt="下一頁"
              />
            </div>
          </div>

          {sharedFooter}
        </main>
      )}

      {isMobile && (
        <main className="mobile-main" style={{ paddingTop: navHeight }}>
          <div className="flow-year-main">
            <div className="selector prev" onClick={handlePrevPage}>
              <img
                src={
                  isPrevDisabled
                    ? "/images/ic-non-select-left.png"
                    : "/images/ic-select-left.png"
                }
                alt="上一頁"
              />
            </div>

            <div className="display">
              <div className="tabs">{tabButtons}</div>

              <div className="container">
                <div
                  className={`book mobile-book ${staticLeftPage?.isTransparent ? "is-cover" : ""}`}
                >
                  {/* 左頁（永遠空白，並隨書本一起被推到畫面外）*/}
                  <div
                    className={`book-page left-page ${staticLeftPage?.isTransparent ? "transparent-page" : ""}`}
                  >
                    <div className="page-inner">
                      {!staticLeftPage?.isBlank && staticLeftPage?.content}
                    </div>
                  </div>

                  {/* 右頁（可視區，顯示當前內容）*/}
                  <div className="book-page right-page">
                    <div className="page-inner">
                      {!staticRightPage?.isBlank && staticRightPage?.content}
                    </div>
                  </div>

                  {/* 下一頁：右頁繞中央書脊往左翻 */}
                  {flipState.isFlipping && flipState.direction === "next" && (
                    <div className="flip-layer flip-next">
                      <div className="flip-front book-page">
                        <div className="page-inner">
                          {!flipState.oldRight?.isBlank &&
                            flipState.oldRight?.content}
                        </div>
                      </div>
                      <div className="flip-back book-page">
                        <div className="page-inner">
                          {!leftPage?.isBlank && leftPage?.content}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 上一頁：左頁從畫面外繞中央書脊往右翻 */}
                  {flipState.isFlipping && flipState.direction === "prev" && (
                    <div className="flip-layer flip-prev">
                      <div className="flip-front book-page">
                        <div className="page-inner">
                          {!flipState.oldLeft?.isBlank &&
                            flipState.oldLeft?.content}
                        </div>
                      </div>
                      <div className="flip-back book-page">
                        <div className="page-inner">
                          {!rightPage?.isBlank && rightPage?.content}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="selector next" onClick={handleNextPage}>
              <img
                src={
                  isNextDisabled
                    ? "/images/ic-non-select-right.png"
                    : "/images/ic-select-right.png"
                }
                alt="下一頁"
              />
            </div>
          </div>

          {sharedFooter}
        </main>
      )}
    </div>
  );
};

export default FlowYearReportComponent;
